/**
 * Thin REST client for the `/tpsl/*` backend proxy.
 *
 * Plain `fetch` rather than the shared `apiFetcher`: creates answer 201 (which
 * apiFetcher rejects) and the executor's 401/409/422/503 bodies carry a machine
 * `code` we want to surface verbatim.
 *
 * NOTE: `/tpsl/*` lives at the backend ROOT, not under the API prefix the
 * codegen endpoints use. `REACT_APP_API_URL` is a bare origin on every chain
 * today, but the origin is derived defensively so a prefixed value can't
 * silently produce `/<prefix>/tpsl/...`.
 */
import { APIEndpointEnum } from "../../enums/endpoint";
import { API_URL } from "../../utils/env";
import { TpslCancelBody, TpslCreateBody, TpslOrder } from "./tpsl.types";

const base = (): string => {
  const raw = String(API_URL || "").replace(/\/+$/, "");
  try {
    return new URL(raw).origin;
  } catch {
    // Relative value (dev proxy) — use as-is.
    return raw;
  }
};

const url = (path: string) => {
  const origin = base();
  // Without an origin these become SAME-ORIGIN requests, which the dev server
  // answers with index.html and a 200 — so a missing REACT_APP_API_URL surfaced as
  // a JSON parse failure ("Could not reach the TP/SL service.") on every call
  // instead of the configuration problem it actually is. Fail with the real cause.
  //
  // Only a genuinely empty value throws: a relative dev-proxy path such as
  // "/perps-api" is truthy and still passes through, as base() intends.
  if (!origin) {
    throw new Error("No perps backend is configured for this network.");
  }
  return `${origin}${path}`;
};

const JSON_HEADERS = { "content-type": "application/json" };

/**
 * Thrown on 503 `{"error":"tpsl unavailable"}` — the runtime signal that this
 * deployment has no trigger executor. Callers hide the TP/SL UI on this.
 */
export class TpslUnavailableError extends Error {
  constructor() {
    super("TP/SL is not available on this network.");
    this.name = "TpslUnavailableError";
  }
}

export const isTpslUnavailable = (error: unknown): boolean =>
  error instanceof TpslUnavailableError;

/** Human-readable messages for the executor's documented failure codes. */
const ERROR_BY_STATUS: Record<number, string> = {
  401: "TP/SL signature rejected. Please sign again with the connected wallet.",
  409: "That TP/SL request was already submitted. Refresh and try again.",
  422: "The TP/SL trigger was rejected by the executor.",
};

async function readJson(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return undefined;
  }
}

async function ensureOk(res: Response, fallback: string): Promise<any> {
  const body = await readJson(res);
  if (res.ok) {
    return body;
  }
  if (res.status === 503) {
    throw new TpslUnavailableError();
  }
  const detail = body?.code || body?.error || body?.message;
  const message = ERROR_BY_STATUS[res.status] || fallback;
  throw new Error(detail ? `${message} (${detail})` : message);
}

export async function getTpslNonce(trader: string): Promise<number> {
  const res = await fetch(url(`${APIEndpointEnum.TPSL_NONCE}/${trader}`));
  const body = await ensureOk(res, "Could not reach the TP/SL service.");
  // The response carries both `nonce` (current) and `nextNonce` — sign nextNonce.
  const nonce = Number(body?.nextNonce);
  if (!Number.isFinite(nonce)) {
    throw new Error("Could not reach the TP/SL service.");
  }
  return nonce;
}

/** `GET /tpsl/orders` answers an envelope: `{ chainId, count, orders }`. */
export async function getTpslOrders(
  trader: string,
  status: string = "open"
): Promise<TpslOrder[]> {
  const query = `?trader=${encodeURIComponent(trader)}&status=${encodeURIComponent(status)}`;
  const res = await fetch(url(`${APIEndpointEnum.TPSL_ORDERS}${query}`));
  const body = await ensureOk(res, "Could not load TP/SL orders.");
  const orders = body?.orders;
  return Array.isArray(orders) ? (orders as TpslOrder[]) : [];
}

export async function createTpslOrders(
  body: TpslCreateBody
): Promise<TpslOrder[]> {
  const res = await fetch(url(APIEndpointEnum.TPSL_ORDERS), {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  const json = await ensureOk(res, "Could not create the TP/SL trigger.");
  if (Array.isArray(json)) {
    return json as TpslOrder[];
  }
  return Array.isArray(json?.orders) ? (json.orders as TpslOrder[]) : [];
}

export async function cancelTpslOrder(
  triggerId: string,
  body: TpslCancelBody
): Promise<void> {
  const res = await fetch(url(`${APIEndpointEnum.TPSL_ORDERS}/${triggerId}`), {
    method: "DELETE",
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  await ensureOk(res, "Could not cancel the TP/SL trigger.");
}
